import { useEffect, useState } from 'react';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';

interface RoomsResponse {
  canRegister: boolean;
  rooms: { reservationId: string }[];
}

/**
 * Shown to the main booker of a booking whose other rooms share their phone or email: those rooms are theirs to look
 * after — share each room's access, or give it its own contact. Opens "My rooms".
 */
export default function RegisterRoomsCard({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    apiClient.get<RoomsResponse>('/api/v1/guest/rooms', { signal: controller.signal })
      .then(({ data }) => setCount(data?.rooms?.length ?? 0))
      .catch(() => setCount(0));
    return () => controller.abort();
  }, []);

  if (count === 0) return null;
  const tr = (key: string, fallback: string, params?: Record<string, string | number>) => {
    const v = t(key, params);
    return v && v !== key ? v : fallback.split('{{n}}').join(String(params?.n ?? ''));
  };

  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-body">
        <h6 className="card-title text-muted mb-2">{tr('rooms.cardTitleManaged', 'Your rooms')}</h6>
        <p className="mb-3 small">
          {tr('rooms.cardBodyManaged', '{{n}} more room(s) on your booking. Share their access with the guests staying there, or give each its own contact so they earn their own points.', { n: count })}
        </p>
        <button className="btn btn-primary w-100" onClick={onOpen}>
          {tr('rooms.cardButtonManaged', 'My rooms')}
        </button>
      </div>
    </div>
  );
}
